// Global
import { pushToDataLayer, DataLayerEvent } from 'components/helpers/Global/Analytics/DataLayer';
import { LinkProps, LinkFieldValue, useSitecore } from '@sitecore-content-sdk/nextjs';
import NextLink from 'next/link';
import React, { forwardRef, useEffect, useRef, useState, useCallback } from 'react';

// Local
import {
  CtaContentResultAnalyticsConfig,
  pushCtaContentResultEvent,
} from 'components/helpers/Analytics/Cta/CtaAnalytics';
import { CtaProps } from 'components/helpers/Atoms/Cta/CtaButton';
import { IconTypes, SvgIcon, SvgIconSize } from 'components/helpers/SvgIcon';
import { GtmEvent } from 'lib/utils/gtm-utils';
import { parseLink } from 'lib/utils/link-utils';
import { useCtaClass } from './useCtaClass';
import useDictionary from 'lib/hooks/useDictionary';
import { isExternalUrl, parseUrlObject, ReplacementToken } from 'lib/utils/string-utils';
import { useRealPathName } from 'lib/hooks/sitecore/useRealPathName';
import { getDownloadUrl, isAsDownload } from 'lib/utils/download-utils';
import { getLocaleUrl, getSiteBaseUrl } from 'lib/utils/domain-locale-utils';

export type LinkContext = 'header' | 'default';

function isRootRelativeLink(href: string): boolean {
  return href.startsWith('/') && !href.startsWith('//');
}

function isLocaleRelativeLink(href: string): boolean {
  return /^[a-z]{2}-[a-z]{2}(?=\/|$)/i.test(href);
}

function hasLeadingLocalePrefix(path: string): boolean {
  return /^\/[a-z]{2}-[a-z]{2}(?=\/|$)/i.test(path);
}

function getCurrentHostname(): string | null {
  if (typeof window === 'undefined') {
    return null;
  }

  return window.location.hostname?.toLowerCase() || null;
}

function collapseDuplicateLocalePrefix(path: string, locale: string): string {
  const normalizedLocale = locale.toLowerCase();
  const duplicateLocalePattern = new RegExp(
    `^/(?:${normalizedLocale})(?:/${normalizedLocale})+(?=/|$)`,
    'i'
  );

  return path.replace(duplicateLocalePattern, `/${normalizedLocale}`);
}

function collapseAnyDuplicateLocalePrefix(path: string): string {
  return path.replace(/^\/([a-z]{2}-[a-z]{2})(?:\/\1)+(?=\/|$)/i, '/$1');
}

function collapseInternalLocalePrefixes(path: string, locale: string): string {
  return collapseAnyDuplicateLocalePrefix(collapseDuplicateLocalePrefix(path, locale));
}

function collapseHrefDuplicateLocalePrefixes(href: string, locale?: string): string {
  if (isLocaleRelativeLink(href)) {
    const relativePath = `/${href}`;
    return locale
      ? collapseInternalLocalePrefixes(relativePath, locale)
      : collapseAnyDuplicateLocalePrefix(relativePath);
  }

  if (isRootRelativeLink(href)) {
    return locale
      ? collapseInternalLocalePrefixes(href, locale)
      : collapseAnyDuplicateLocalePrefix(href);
  }

  try {
    const parsedHref = new URL(href);
    const originalPath = `${parsedHref.pathname}${parsedHref.search}${parsedHref.hash}`;
    const collapsedPath = locale
      ? collapseInternalLocalePrefixes(originalPath, locale)
      : collapseAnyDuplicateLocalePrefix(originalPath);

    if (collapsedPath !== originalPath) {
      return collapsedPath;
    }
  } catch {
    return href;
  }

  return href;
}

function relativizeCurrentHostAbsoluteHref(href: string): string {
  try {
    const parsedHref = new URL(href);
    const currentHostname = getCurrentHostname();

    if (currentHostname && parsedHref.hostname.toLowerCase() === currentHostname) {
      return `${parsedHref.pathname}${parsedHref.search}${parsedHref.hash}`;
    }
  } catch {
    return href;
  }

  return href;
}

function isSameSiteAbsoluteLink(href: string, siteName: string, locale: string): boolean {
  try {
    const parsedHref = new URL(href);
    const currentHostname = getCurrentHostname();

    if (currentHostname && parsedHref.hostname.toLowerCase() === currentHostname) {
      return true;
    }

    const siteBaseUrl = getSiteBaseUrl(siteName, locale);

    if (!siteBaseUrl) {
      return false;
    }

    return parsedHref.hostname.toLowerCase() === new URL(siteBaseUrl).hostname.toLowerCase();
  } catch {
    return false;
  }
}

// Determines whether `href` points off the CURRENT site's own domain, instead of comparing
// against the single, global `PUBLIC_URL` (this app builds one deployment that serves many
// site domains via next.config.js i18n.domains, so PUBLIC_URL is only one of many valid
// "internal" domains -- a link to a sibling brand domain was wrongly read as internal whenever
// it happened to match PUBLIC_URL). Deliberately avoids `window.location` (see e0522bd56, which
// removed a window-based check here to fix a hydration mismatch) so server and client agree.
function isExternalToSiteDomain(href: string, siteName?: string, locale?: string): boolean {
  let parsedHref: URL;
  try {
    parsedHref = new URL(href);
  } catch {
    return false;
  }

  if (!siteName || !locale) {
    return isExternalUrl(href);
  }

  const siteBaseUrl = getSiteBaseUrl(siteName, locale);
  if (!siteBaseUrl) {
    return isExternalUrl(href);
  }

  try {
    return parsedHref.hostname.toLowerCase() !== new URL(siteBaseUrl).hostname.toLowerCase();
  } catch {
    return isExternalUrl(href);
  }
}

function normalizeInternalHref(href: string, siteName?: string, locale?: string): string {
  const trimmedHref = href.trim();

  if (!trimmedHref) {
    return href;
  }

  if (trimmedHref.startsWith('/api/')) {
    return href;
  }

  const duplicateLocaleCollapsedHref = collapseHrefDuplicateLocalePrefixes(trimmedHref, locale);

  if (duplicateLocaleCollapsedHref !== trimmedHref) {
    return duplicateLocaleCollapsedHref;
  }

  if (!siteName || !locale) {
    const sameHostRelativeHref = relativizeCurrentHostAbsoluteHref(trimmedHref);

    if (sameHostRelativeHref !== trimmedHref) {
      return sameHostRelativeHref;
    }

    return href;
  }

  try {
    if (isLocaleRelativeLink(trimmedHref)) {
      const normalizedHref = getLocaleUrl(
        siteName,
        locale,
        collapseInternalLocalePrefixes(`/${trimmedHref}`, locale),
        true
      );

      return normalizedHref;
    }

    if (isRootRelativeLink(trimmedHref)) {
      const normalizedHref = getLocaleUrl(
        siteName,
        locale,
        collapseInternalLocalePrefixes(trimmedHref, locale),
        true
      );

      return normalizedHref;
    }

    const parsedHref = new URL(trimmedHref);
    const collapsedPath = collapseInternalLocalePrefixes(
      `${parsedHref.pathname}${parsedHref.search}${parsedHref.hash}`,
      locale
    );

    if (collapsedPath !== `${parsedHref.pathname}${parsedHref.search}${parsedHref.hash}`) {
      return collapsedPath;
    }

    if (!isSameSiteAbsoluteLink(trimmedHref, siteName, locale)) {
      return href;
    }

    const localizedHref = getLocaleUrl(
      siteName,
      locale,
      `${parsedHref.pathname}${parsedHref.search}${parsedHref.hash}`,
      true
    );

    return localizedHref;
  } catch {
    return href;
  }
}

export type CtaLinkProps = Omit<LinkProps, 'field' | 'href'> &
  CtaProps & {
    url?: string;
    text?: string;
    anchor?: string;
    contentResultAnalytics?: CtaContentResultAnalyticsConfig;
    querystring?: string;
    gtmEvent?: GtmEvent;
    srOnlyText?: string;
    suppressNewTabIcon?: boolean;
    showLinkTextWithChildrenPresent?: boolean;
    linkContext?: LinkContext;
  };
const CtaLink = forwardRef<HTMLAnchorElement, CtaLinkProps>(
  (originalProps: CtaLinkProps, ref): JSX.Element | null => {
    const {
      onClick,
      children,
      className,
      ctaStyle,
      ctaIconSize = 'sm',
      ctaIconBefore,
      ctaIconAfter,
      ctaIconNoSpacer,
      ctaIconClassName,
      ctaVariant,
      ctaComponentClass,
      ctaSize,
      url,
      text,
      target,
      title,
      anchor,
      contentResultAnalytics,
      querystring,
      gtmEvent,
      showLinkTextWithChildrenPresent = false,
      srOnlyText,
      suppressNewTabIcon,
      linkContext = 'default',
      ...props
    } = originalProps;

    // Add noopener/noreferrer for target="_blank" links to prevent reverse tabnabbing (Invicti #6).
    const { rel: callerRel, ...propsWithoutRel } = props as typeof props & { rel?: string };
    const effectiveRel: string | undefined =
      target === '_blank'
        ? [
            'noopener',
            'noreferrer',
            ...(callerRel
              ?.split(/\s+/)
              .filter((r) => r && !['noopener', 'noreferrer'].includes(r)) ?? []),
          ].join(' ')
        : callerRel;

    const { getDictionaryValue } = useDictionary();
    const { page } = useSitecore();
    const ctaClass = useCtaClass(ctaStyle, className, ctaComponentClass, ctaVariant, ctaSize);
    const siteName = page?.layout?.sitecore?.context?.site?.name;
    const locale = page?.layout?.sitecore?.context?.language || page?.locale;
    const fieldValue: LinkFieldValue = {
      href: url,
      text: text,
      anchor,
      querystring,
      target,
      title,
    };
    const { realText, shouldRender, isInternalAnchor, isCustomProtocol, parsedUrl } = parseLink(
      fieldValue,
      children,
      showLinkTextWithChildrenPresent
    );
    const normalizedHref = normalizeInternalHref(parsedUrl?.href ?? '', siteName, locale);
    const normalizedParsedUrl =
      normalizedHref && normalizedHref !== parsedUrl?.href
        ? parseUrlObject(normalizedHref)
        : parsedUrl;
    const disableNextLocalePrefix =
      normalizedHref !== (parsedUrl?.href ?? '') ||
      hasLeadingLocalePrefix(normalizedParsedUrl?.pathname ?? '');
    const shouldBypassNextLink = disableNextLocalePrefix;

    const pathname = useRealPathName();
    const urlPathname = normalizedParsedUrl?.pathname ?? '';
    const isCurrentPage = urlPathname === pathname;
    const anchorRef = useRef<HTMLAnchorElement | null>(null);
    const [hasImageInDom, setHasImageInDom] = useState<boolean>(false);
    const setAnchorRef = useCallback(
      (node: HTMLAnchorElement | null) => {
        anchorRef.current = node;
        if (typeof ref === 'function') {
          ref(node);
        } else if (ref) {
          (ref as React.MutableRefObject<HTMLAnchorElement | null>).current = node;
        }
      },
      [ref]
    );
    useEffect(() => {
      const el = anchorRef.current;
      if (!el) return;
      setHasImageInDom(!!el.querySelector('img'));
    }, [children]);
    const handleOnClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
      pushCtaContentResultEvent({
        contentListName: contentResultAnalytics?.contentListName,
        linkUrl: normalizedParsedUrl?.href,
      });

      if (url) {
        const gtmEventInner = {
          ...gtmEvent,
          'gtm.element.dataset.gtmLinkName': realText || title,
          'gtm.element.dataset.gtmLinkUrl': normalizedParsedUrl?.href,
        };
        pushToDataLayer(gtmEventInner as DataLayerEvent);
      }
      if (onClick) onClick(e);
    };
    const isDownloadLink = isAsDownload(normalizedParsedUrl);
    const hrefString = normalizedParsedUrl?.href ?? '';
    const isExternalDomain = isExternalToSiteDomain(hrefString, siteName, locale);
    const isMailtoLink = hrefString.trim().toLowerCase().startsWith('mailto:');
    if (!shouldRender || !normalizedParsedUrl) {
      return <></>;
    }

    // Icon suppression
    const hideIconsForHeader = linkContext === 'header';

    const effectiveSuppressNewTabIcon =
      suppressNewTabIcon || hasImageInDom || isDownloadLink || hideIconsForHeader || isMailtoLink;

    const effectiveCtaIconAfter: IconTypes | undefined =
      hideIconsForHeader || hasImageInDom || isMailtoLink
        ? undefined
        : (ctaIconAfter ?? (isDownloadLink ? ('download' as IconTypes) : undefined));

    // NextLink plays weird with download attribute, so we use a normal <a> tag instead
    if (
      isInternalAnchor ||
      isCustomProtocol ||
      isCurrentPage ||
      isDownloadLink ||
      shouldBypassNextLink
    ) {
      return (
        <a
          {...propsWithoutRel}
          rel={effectiveRel}
          className={ctaClass}
          data-component="helpers/sitecorewrappers/linkwrapper"
          href={
            isInternalAnchor
              ? normalizedParsedUrl.hash
              : isDownloadLink
                ? getDownloadUrl(normalizedParsedUrl.href)
                : normalizedParsedUrl.href
          }
          onClick={handleOnClick}
          ref={setAnchorRef}
          title={title}
          target={isDownloadLink ? '_self' : target}
          download={isDownloadLink ? true : undefined}
        >
          {ctaIconBefore && (
            <SvgIcon
              className={ctaIconClassName || 'cta-icon-default'}
              icon={ctaIconBefore}
              size={ctaIconSize}
              aria-hidden="true"
              iconPosition={ctaIconNoSpacer ? 'none' : 'before'}
            />
          )}
          {!children ? (
            <span>
              {realText ||
                (normalizedParsedUrl.href?.startsWith('/')
                  ? normalizedParsedUrl.href.slice(1)
                  : normalizedParsedUrl.href)}
            </span>
          ) : realText ? (
            <span>{realText}</span>
          ) : (
            <></>
          )}
          {children}
          <ScreenReaderOnlyTextAndNewTabIcon
            target={target}
            srOnlyText={srOnlyText}
            suppressNewTabIcon={effectiveSuppressNewTabIcon}
            ctaIconSize={ctaIconSize}
            ctaIcon={effectiveCtaIconAfter}
            ctaIconNoSpacer={ctaIconNoSpacer}
            getDictionaryValue={getDictionaryValue}
            isExternalDomain={isExternalDomain}
            isDownloadLink={isDownloadLink}
          />
        </a>
      );
    }
    return (
      <NextLink
        {...propsWithoutRel}
        rel={effectiveRel}
        className={ctaClass}
        data-component="helpers/sitecorewrappers/linkwrapper"
        href={normalizedParsedUrl}
        locale={disableNextLocalePrefix ? false : undefined}
        onClick={handleOnClick}
        ref={setAnchorRef}
        target={target}
        title={title}
      >
        {ctaIconBefore && (
          <SvgIcon
            className={ctaIconClassName || 'cta-icon-default'}
            icon={ctaIconBefore}
            size={ctaIconSize}
            aria-hidden="true"
            iconPosition={ctaIconNoSpacer ? 'none' : 'before'}
          />
        )}
        {realText ? <span>{realText}</span> : null}
        {children}
        <ScreenReaderOnlyTextAndNewTabIcon
          target={target}
          srOnlyText={srOnlyText}
          suppressNewTabIcon={effectiveSuppressNewTabIcon}
          ctaIconSize={ctaIconSize}
          ctaIcon={effectiveCtaIconAfter}
          getDictionaryValue={getDictionaryValue}
          isExternalDomain={isExternalDomain}
          isDownloadLink={isDownloadLink}
        />
      </NextLink>
    );
  }
);

CtaLink.displayName = 'CtaLink';
export default CtaLink;
function ScreenReaderOnlyTextAndNewTabIcon({
  ctaIconSize = 'sm',
  srOnlyText,
  suppressNewTabIcon,
  target,
  ctaIcon,
  ctaIconNoSpacer,
  getDictionaryValue,
  isExternalDomain,
  isDownloadLink,
}: {
  ctaIconSize?: SvgIconSize;
  srOnlyText?: string;
  suppressNewTabIcon?: boolean;
  target?: string;
  ctaIcon?: IconTypes;
  ctaIconNoSpacer?: boolean;
  getDictionaryValue?: (key: string, fallback?: string, tokens?: ReplacementToken[]) => string;
  isExternalDomain?: boolean;
  isDownloadLink?: boolean;
}) {
  const isExternal = !!isExternalDomain;
  const opensInNewTab = target === '_blank';
  const getDictionaryDefault = (key: string, fallback?: string) => {
    try {
      return getDictionaryValue ? getDictionaryValue(key, fallback) : fallback || '';
    } catch {
      return fallback || '';
    }
  };
  // Only show external icon when both conditions are true
  const Icon = ctaIcon ? (
    <SvgIcon icon={ctaIcon} size={ctaIconSize} iconPosition={ctaIconNoSpacer ? 'none' : 'after'} />
  ) : !suppressNewTabIcon && isExternal && opensInNewTab ? (
    <SvgIcon
      viewBox="0 0 24 24"
      icon="new-tab"
      size={ctaIconSize}
      iconPosition={ctaIconNoSpacer ? 'none' : 'after'}
    />
  ) : null;
  const newTabText = getDictionaryDefault('Common.OpensInNewTabs', '(Opens in a new tab)');
  const downloadText = getDictionaryDefault('Common.DownloadsAFile', 'downloads a file');
  const srAnnouncement = isDownloadLink
    ? downloadText
    : `${srOnlyText ? srOnlyText : ''}${opensInNewTab ? ` ${newTabText}` : ''}`.trim();
  if (!Icon && !srAnnouncement) {
    return <></>;
  }
  return (
    <>
      {Icon}
      {srAnnouncement && <span className="screenReaderText"> {srAnnouncement}</span>}
    </>
  );
}
